import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useGroups } from '@/lib/hooks/useGroups';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { useInviteUser } from '@/lib/hooks/useUserProfile';
import {
  groupGroupsByParentRole,
  getGroupRoleTone,
} from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import type { UserRole } from '@/lib/types/database';

export interface InviteUserFormProps {
  /** Optional callback when invite is successfully sent */
  onSuccess?: () => void;
}

export function InviteUserForm({ onSuccess }: InviteUserFormProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>('fellow');
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([]);
  const inviteUserMutation = useInviteUser();
  const groupsQuery = useGroups(false);
  const { options: roleOptions } = useRoleDirectory();
  const { toast, showToast, hideToast } = useToastNotification();
  const groupSections = groupGroupsByParentRole(groupsQuery.data ?? []);

  const toggleGroupKey = (importKey: string) => {
    setSelectedGroupKeys((current) =>
      current.includes(importKey)
        ? current.filter((key) => key !== importKey)
        : [...current, importKey]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await inviteUserMutation.mutateAsync({
        email,
        role,
        fullName: fullName || undefined,
        groupImportKeys: selectedGroupKeys,
      });
      showToast({
        title: 'Invite sent',
        description: `An invite has been sent to ${email}`,
        variant: 'success',
      });
      // Reset form
      setEmail('');
      setFullName('');
      setRole('fellow'); // default to fellow role
      setSelectedGroupKeys([]);
      // Call success callback if provided
      onSuccess?.();
    } catch (error) {
      showToast({
        title: 'Failed to send invite',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'error',
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
            disabled={inviteUserMutation.isPending}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name *</Label>
          <Input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="First and Last Name"
            required
            disabled={inviteUserMutation.isPending}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role *</Label>
          <Select
            value={role}
            onValueChange={(value) => setRole(value as UserRole)}
          >
            <SelectTrigger id="role" disabled={inviteUserMutation.isPending}>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((roleOption) => (
                <SelectItem key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">Controls app permissions.</p>
        </div>

        <div className="space-y-3 rounded-lg border border-slate-500 bg-slate-600/50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="uppercase tracking-normal text-xs text-slate-300">
                Starting groups{' '}
                <span className="font-normal normal-case text-slate-400">
                  · optional
                </span>
              </Label>
              <p className="mt-1 text-xs text-slate-400">
                {selectedGroupKeys.length}{' '}
                {selectedGroupKeys.length === 1 ? 'selected' : 'selected'} ·
                active groups only.
              </p>
            </div>
          </div>

          {groupsQuery.isLoading && (
            <p className="text-sm text-slate-400">Loading groups...</p>
          )}

          {groupsQuery.isError && (
            <p className="text-sm text-red-300">Could not load groups.</p>
          )}

          {!groupsQuery.isLoading &&
            !groupsQuery.isError &&
            groupSections.length === 0 && (
              <p className="text-sm text-slate-400">
                No active groups available.
              </p>
            )}

          {groupSections.map((section) => {
            const tone = getGroupRoleTone(section.parentRole);
            return (
              <div key={section.parentRole} className="space-y-2">
                <div
                  className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${tone.pillClassName}`}
                >
                  {getRoleLabelByKey(section.parentRole)}
                </div>
                <div className="space-y-1">
                  {section.groups.map((group) => {
                    const checked = selectedGroupKeys.includes(
                      group.import_key
                    );

                    return (
                      <label
                        key={group.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                          checked ? 'bg-slate-500/70' : 'hover:bg-slate-600'
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() =>
                            toggleGroupKey(group.import_key)
                          }
                          disabled={inviteUserMutation.isPending}
                        />
                        <span className="min-w-0 flex-1 truncate text-slate-100">
                          {group.display_name}
                        </span>
                        <span className="font-mono text-xs text-slate-400">
                          {group.import_key}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <Button
          type="submit"
          disabled={inviteUserMutation.isPending}
          className="w-full"
          size="lg"
        >
          {inviteUserMutation.isPending ? 'Sending...' : 'Send Invite'}
        </Button>
      </form>

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
